import NP from 'number-precision';
import { useState, useRef, useCallback, useEffect } from 'react';
import { isNumber } from '../../../utils/is.js';
import { ArrowDown, ArrowUp } from '../../../utils/keycode.js';

// Value's auto change speed when user holds on plus or minus
const AUTO_CHANGE_INTERVAL = 200;

// Delay to auto change value when user holds on plus or minus
const AUTO_CHANGE_START_DELAY = 1000;
const useInputNumber = ({
  precision,
  step,
  defaultValue,
  inputVal,
  onChange,
  min,
  max,
  disabled,
  formatter,
  parser,
  onKeyDown,
  onFocus,
  onBlur,
  readOnly,
  hideControl,
  mode
}) => {
  const mergedPrecision = (() => {
    if (isNumber(precision)) {
      const decimal = `${step}`.split('.')[1];
      const stepPrecision = decimal && decimal.length || 0;
      return Math.max(stepPrecision, precision);
    }
    return null;
  })();
  const initialValue = min > defaultValue ? min : defaultValue;
  const [innerValue, setInnerValue] = useState(initialValue);
  const numValue = (() => {
    const mergedValue = inputVal || innerValue;
    return typeof mergedValue === 'string' && mergedValue !== '' ? +mergedValue : mergedValue;
  })();
  const [inputValue, setInputValue] = useState('');
  const [isOutOfRange, setIsOutOfRange] = useState(false);
  const [isUserInputting, setIsUserInputting] = useState(false);

  // Value is not set
  const isEmptyValue = numValue === '' || numValue === undefined || numValue === null;
  const refAutoTimer = useRef(null);
  const refInput = useRef(null);
  // Ref to keep track of whether user has taken operations since the last change of prop value
  const refHasOperateSincePropValueChanged = useRef(false);
  const setValue = useCallback(newVal => {
    setInnerValue(newVal);
    const newValue = isNumber(+newVal) ? +newVal : undefined;
    if (newValue !== numValue) {
      onChange && onChange(newValue);
    }
  }, [numValue, onChange]);
  const stop = () => {
    refAutoTimer.current && clearTimeout(refAutoTimer.current);
    refAutoTimer.current = null;
  };
  const getLegalValue = useCallback(changedValue => {
    let finalValue = Number(changedValue);
    if (!changedValue && changedValue !== 0) {
      finalValue = undefined;
    } else if (!isNumber(finalValue)) {
      finalValue = changedValue === '-' ? changedValue : '';
    }
    if (finalValue < min) {
      finalValue = min;
    }
    if (finalValue > max) {
      finalValue = max;
    }
    const pres = isNumber(mergedPrecision) ? Number(finalValue).toFixed(mergedPrecision) : finalValue;
    return isNumber(finalValue) ? pres : undefined;
  }, [min, max, mergedPrecision]);
  useEffect(() => {
    return () => stop();
  }, []);
  useEffect(() => {
    refHasOperateSincePropValueChanged.current = false;
  }, [inputVal]);
  useEffect(() => {
    const _isOutOfRange = isNumber(min) && numValue < min || isNumber(max) && numValue > max;

    // Don't correct the illegal value caused by prop value. Wait for user to take actions.
    if (_isOutOfRange && refHasOperateSincePropValueChanged.current) {
      setValue(getLegalValue(numValue));
    }
    setIsOutOfRange(_isOutOfRange);
  }, [min, max, numValue, getLegalValue, setValue]);
  const handleArrowKey = (event, method, needRepeat = false) => {
    event.persist();
    event.preventDefault();
    setIsUserInputting(false);
    if (disabled) {
      return;
    }
    let finalValue = min === -Infinity ? 0 : min;
    if (!isEmptyValue) {
      finalValue = NP[method](numValue, step);
    }
    setValue(getLegalValue(finalValue));
    refInput.current && refInput.current.focus();

    // auto change while holding
    if (needRepeat) {
      const isFirstRepeat = refAutoTimer.current === null;
      refAutoTimer.current = setTimeout(() => event.target.dispatchEvent(event.nativeEvent), isFirstRepeat ? AUTO_CHANGE_START_DELAY : AUTO_CHANGE_INTERVAL);
    }
  };
  const getDisplayInputValue = () => {
    let _value;
    if (isUserInputting) {
      _value = inputValue;
    } else if (isNumber(numValue) && isNumber(mergedPrecision)) {
      _value = numValue.toFixed(mergedPrecision);
    } else if (numValue == null) {
      _value = '';
    } else {
      _value = numValue.toString();
    }
    return formatter ? formatter(_value) : _value;
  };
  const inputEventHandlers = {
    onChange: value => {
      setIsUserInputting(true);
      let targetValue = value.trim().replace(/。/g, '.');
      targetValue = parser ? parser(targetValue) : targetValue;
      if (isNumber(+targetValue) || targetValue === '-' || !targetValue || targetValue === '.') {
        const formatValue = getLegalValue(targetValue);
        setInputValue(targetValue);
        setValue(formatValue);
      }
    },
    onKeyDown: e => {
      e.stopPropagation();
      const key = e.key;
      if (key === ArrowDown.key) {
        handleArrowKey(e, 'minus');
      } else if (key === ArrowUp.key) {
        handleArrowKey(e, 'plus');
      }
      stop();
      onKeyDown && onKeyDown(e);
    },
    onFocus: e => {
      // Both tab and button click trigger focus event. This can be used to determine whether user has taken operations
      refHasOperateSincePropValueChanged.current = true;
      setInputValue(refInput?.current?.current?.value);
      onFocus && onFocus(e);
    },
    onBlur: e => {
      setValue(getLegalValue(numValue));
      setIsUserInputting(false);
      onBlur && onBlur(e);
    }
  };
  const getControlButtonEventsHandlers = method => {
    return readOnly ? {} : {
      onMouseDown: e => handleArrowKey(e, method, true),
      onMouseLeave: stop,
      onMouseUp: stop
    };
  };
  const shouldRenderButton = !hideControl && mode === 'button';
  const shouldRenderLayer = !hideControl && !readOnly && mode === 'embed';
  return {
    mergedPrecision,
    innerValue,
    setInnerValue,
    numValue,
    inputValue,
    setInputValue,
    isOutOfRange,
    setIsOutOfRange,
    isUserInputting,
    setIsUserInputting,
    isEmptyValue,
    refAutoTimer,
    refInput,
    refHasOperateSincePropValueChanged,
    setValue,
    stop,
    getLegalValue,
    handleArrowKey,
    getDisplayInputValue,
    inputEventHandlers,
    getControlButtonEventsHandlers,
    shouldRenderButton,
    shouldRenderLayer
  };
};

export { useInputNumber };
